/**
 * FeedbackDialog — v1.2.34
 *
 * The report form invoked by <FeedbackBubble>. Captures a short note
 * plus optional metadata and POSTs to /api/feedback.
 *
 * Fields:
 *   - Type      (bug | feature_request | question)
 *   - Severity  (low | medium | high)
 *   - Note      (required, ≤2000 chars)
 *   - Notify me when resolved (checkbox, default ON)
 *   - Email     (shown only when unauth'd + notify=true)
 *   - Attach screenshot (checkbox — captures current viewport via html2canvas)
 */
import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import { AlertCircle, Bug, Lightbulb, HelpCircle, Camera } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_OPTIONS = [
  { value: 'bug', label: 'Bug', icon: Bug, tint: 'text-rose-600 bg-rose-50 border-rose-200' },
  { value: 'feature_request', label: 'Feature', icon: Lightbulb, tint: 'text-amber-600 bg-amber-50 border-amber-200' },
  { value: 'question', label: 'Question', icon: HelpCircle, tint: 'text-sky-600 bg-sky-50 border-sky-200' },
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

export const FeedbackDialog = ({ open, onOpenChange, pageUrl, breadcrumb = [] }) => {
  const { user } = useAuth() || {};
  const isAuthenticated = !!user?.id;

  const [type, setType] = useState('bug');
  const [severity, setSeverity] = useState('medium');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [attachScreenshot, setAttachScreenshot] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const submittedRef = useRef(false);

  const pageTitle = useMemo(() => (typeof document !== 'undefined' ? document.title : ''), [open]);

  const resetForm = () => {
    setType('bug');
    setSeverity('medium');
    setNote('');
    setNotify(true);
    setAttachScreenshot(false);
    setEmail('');
    setSubmitted(false);
    submittedRef.current = false;
  };

  const handleClose = (val) => {
    onOpenChange(val);
    if (!val) {
      // Reset after a beat so the "thanks" state doesn't flash on close.
      setTimeout(resetForm, 300);
    }
  };

  const captureScreenshot = async () => {
    try {
      const target = document.querySelector('[data-testid="main-content"]') || document.body;
      const canvas = await html2canvas(target, {
        backgroundColor: '#ffffff',
        scale: Math.min(1, window.devicePixelRatio || 1),
        logging: false,
        useCORS: true,
        allowTaint: true,
      });
      // Cap width to keep the payload small.
      const MAX = 1280;
      let finalCanvas = canvas;
      if (canvas.width > MAX) {
        const ratio = MAX / canvas.width;
        finalCanvas = document.createElement('canvas');
        finalCanvas.width = MAX;
        finalCanvas.height = Math.round(canvas.height * ratio);
        finalCanvas.getContext('2d').drawImage(canvas, 0, 0, finalCanvas.width, finalCanvas.height);
      }
      return finalCanvas.toDataURL('image/jpeg', 0.7);
    } catch (e) {
      console.warn('[feedback] screenshot failed', e);
      return null;
    }
  };

  const handleSubmit = async () => {
    if (submittedRef.current) return;
    const trimmed = note.trim();
    if (trimmed.length < 3) {
      toast.error('Please describe the issue (at least 3 characters).');
      return;
    }
    if (!isAuthenticated && notify && !email.trim()) {
      toast.error('Enter your email so we can let you know when this is resolved.');
      return;
    }

    setSubmitting(true);
    submittedRef.current = true;

    let screenshotBase64 = null;
    if (attachScreenshot) {
      screenshotBase64 = await captureScreenshot();
    }

    const payload = {
      note: trimmed,
      type,
      severity,
      page_url: pageUrl || window.location.pathname + window.location.search,
      page_title: pageTitle,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      breadcrumb,
      notify_requester: notify,
      screenshot_base64: screenshotBase64,
      reporter_email: isAuthenticated ? null : (email.trim() || null),
    };

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/api/feedback`, payload, { headers });
      setSubmitted(true);
      toast.success('Thanks — your report was received.');
    } catch (err) {
      submittedRef.current = false;
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        toast.error('You’ve submitted a lot recently — please wait a bit before sending another.');
      } else if (status === 422 && detail) {
        toast.error(detail);
      } else {
        toast.error('Could not submit report. Please try again in a moment.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="feedback-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle size={18} className="text-teal-600" />
            {submitted ? 'Feedback received' : 'Send Feedback'}
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="space-y-4" data-testid="feedback-thanks">
            <p className="text-sm text-slate-600">
              Thanks — a founder or admin will take a look. You’ll get an email
              here if you asked to be notified when it’s resolved.
            </p>
            <div className="flex justify-end">
              <Button
                onClick={() => handleClose(false)}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="feedback-close-btn"
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Type chips */}
            <div>
              <p className="text-xs font-medium text-slate-600 mb-1.5">Type</p>
              <div className="grid grid-cols-3 gap-2">
                {TYPE_OPTIONS.map(({ value, label, icon: Icon, tint }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setType(value)}
                    className={`flex flex-col items-center justify-center gap-1 py-2.5 rounded-lg border text-xs font-medium transition-all ${
                      type === value
                        ? tint + ' ring-2 ring-offset-1 ring-slate-300'
                        : 'text-slate-600 bg-white border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`feedback-type-${value}`}
                  >
                    <Icon size={16} />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Severity */}
            <div>
              <p className="text-xs font-medium text-slate-600 mb-1.5">Severity</p>
              <div className="flex gap-2">
                {SEVERITY_OPTIONS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setSeverity(value)}
                    className={`flex-1 py-2 rounded-lg border text-xs font-medium transition-all ${
                      severity === value
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`feedback-severity-${value}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Note */}
            <div>
              <p className="text-xs font-medium text-slate-600 mb-1.5">
                What happened? <span className="text-rose-500">*</span>
              </p>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 2000))}
                placeholder={
                  type === 'bug'
                    ? 'What did you expect? What actually happened? Steps to reproduce…'
                    : type === 'feature_request'
                    ? 'What would you like to see? Why would it help?'
                    : 'What are you trying to figure out?'
                }
                rows={5}
                data-testid="feedback-note"
                className="text-sm"
              />
              <p className="text-[10px] text-slate-400 mt-1 text-right">
                {note.length} / 2000
              </p>
            </div>

            {/* Context strip — read-only */}
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-500 space-y-0.5">
              <div>
                <span className="text-slate-400">Page:</span>{' '}
                <span className="font-mono text-slate-700 break-all">
                  {pageUrl || window.location.pathname}
                </span>
              </div>
              {isAuthenticated && (
                <div>
                  <span className="text-slate-400">From:</span>{' '}
                  <span className="text-slate-700">
                    {user?.full_name} ({user?.role})
                  </span>
                </div>
              )}
              {!isAuthenticated && (
                <div className="text-slate-500 italic">
                  You’re not signed in — this will be recorded anonymously.
                </div>
              )}
            </div>

            {/* Options */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                  className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  data-testid="feedback-notify"
                />
                Notify me by email when this is resolved
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={attachScreenshot}
                  onChange={(e) => setAttachScreenshot(e.target.checked)}
                  className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  data-testid="feedback-attach-screenshot"
                />
                <Camera size={13} className="text-slate-500" />
                Attach a screenshot of what I’m seeing
              </label>
            </div>

            {/* Email for anonymous notify */}
            {!isAuthenticated && notify && (
              <div>
                <p className="text-xs font-medium text-slate-600 mb-1.5">
                  Your email <span className="text-rose-500">*</span>
                </p>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="feedback-email"
                  className="text-sm"
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={submitting}
                data-testid="feedback-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || note.trim().length < 3}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="feedback-submit-btn"
              >
                {submitting ? 'Sending…' : 'Send report'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
